import Text from 'components/Text/Text';
import { ArchivedLabel } from 'components/Select/ArchivedLabel';
import React, { useContext, useState, useMemo, useEffect, useCallback } from 'react';
import { AppContext, IAppContext } from 'appState/appContext';
import { IItemForGetShortInfoWithStatus, UserRoles, EntityStatus } from 'types';
import { useTranslation } from 'react-i18next';
import { TEditUserSelectedItem } from '../../../../../../../editUserState/editUserContext';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { SHOW_ARCHIVED_RESTAURANTS } from 'appConstants';
import { Checkbox } from 'antd';
import SearchField from 'components/SearchField/SearchField';
import { Rule } from 'antd/lib/form';
import { ISelectOption } from 'components/Select/types';
import FormSelect from 'components/Select/FormSelect';
import style from './MultipleSelect.module.scss';

interface IMultipleSelect {
	data: IItemForGetShortInfoWithStatus[];
	isLoading: boolean;
	refetch: () => void;
	responseError: boolean;
	setIDsRestaurant: (value: string[]) => void;
	IDSelectedCustomer: TEditUserSelectedItem;
	IDsSelectedRestaurant: string[];
}

const MultipleSelect: React.FC<IMultipleSelect> = ({
	data,
	isLoading,
	refetch,
	responseError,
	setIDsRestaurant,
	IDSelectedCustomer,
	IDsSelectedRestaurant,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const [searchString, setSearchString] = useState<string>('');
	const [showArchived, setShowArchived] = useState<boolean>(
		localStorage.getItem(SHOW_ARCHIVED_RESTAURANTS) === 'true',
	);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(() => [{ required: true, message: t('selectOneRestaurant') }], [t]);

	const visibleData = useMemo(() => {
		return showArchived
			? data
			: data.filter((el) => el.status === EntityStatus.Active || IDsSelectedRestaurant.includes(el.id));
	}, [data, showArchived, IDsSelectedRestaurant]);

	const accessibleRestaurants = useMemo(() => {
		const restaurants = loggedUser.restaurants || [];
		return showArchived
			? restaurants
			: restaurants.filter((el) => el.status === EntityStatus.Active || IDsSelectedRestaurant.includes(el.id));
	}, [loggedUser, showArchived, IDsSelectedRestaurant]);

	const filteredData = useMemo(() => {
		return searchString
			? visibleData.filter((el) => el.name.toLowerCase().includes(searchString.toLowerCase()))
			: visibleData;
	}, [searchString, visibleData]);

	const mappedOptions = useMemo<ISelectOption[]>(() => {
		const accessibleRestaurantIds = accessibleRestaurants.map((el) => el.id);
		return filteredData.map((el) => ({
			id: el.id,
			name: el.name,
			disabled: !hasAccess && !accessibleRestaurantIds.includes(el.id),
			additionalContent: el.status === EntityStatus.Archived ? <ArchivedLabel /> : undefined,
		}));
	}, [filteredData, accessibleRestaurants, hasAccess]);

	const handleSelectAllData = useCallback(() => {
		if (hasAccess) {
			if (visibleData.length === IDsSelectedRestaurant.length) {
				setIDsRestaurant([]);
			} else {
				const IDsOfAllRestaurants = visibleData.map((el) => el.id);
				setIDsRestaurant(IDsOfAllRestaurants);
			}
		} else {
			const accessibleRestaurantIds = accessibleRestaurants.map((el) => el.id);
			if (accessibleRestaurantIds.every((el) => IDsSelectedRestaurant.includes(el))) {
				const filteredIDsSelectedRestaurant = IDsSelectedRestaurant.filter(
					(el) => !accessibleRestaurantIds.includes(el),
				);
				setIDsRestaurant(filteredIDsSelectedRestaurant);
			} else {
				const filteredAccessibleRestaurantIds = accessibleRestaurantIds.filter(
					(el) => !IDsSelectedRestaurant.includes(el),
				);
				setIDsRestaurant([...IDsSelectedRestaurant, ...filteredAccessibleRestaurantIds]);
			}
		}
	}, [hasAccess, visibleData, IDsSelectedRestaurant, setIDsRestaurant, accessibleRestaurants]);

	const toggleShowArchived = useCallback(() => {
		localStorage.setItem(SHOW_ARCHIVED_RESTAURANTS, JSON.stringify(!showArchived));
		setShowArchived((prevValue) => !prevValue);
	}, [showArchived]);

	useEffect(() => {
		setSearchString('');
	}, [IDSelectedCustomer]);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => {
			const getIsCheckedByHasAccess = () => {
				return !!(visibleData.length && visibleData.length === IDsSelectedRestaurant.length);
			};

			const getIsCheckedByWithoutAccess = () => {
				const accessibleRestaurantIds = accessibleRestaurants.map((el) => el.id);
				return accessibleRestaurantIds.every((el) => IDsSelectedRestaurant.includes(el));
			};

			return (
				<>
					<div className={style.selectMenuHeader}>
						<Checkbox
							className={style.checkBox}
							checked={hasAccess ? getIsCheckedByHasAccess() : getIsCheckedByWithoutAccess()}
							disabled={hasAccess ? !visibleData.length : !accessibleRestaurants.length}
							onChange={handleSelectAllData}>
							<Text
								translationNameSpace="form"
								translationKey="selectAllRestaurantsLabel"
								asFragment={true}
							/>
						</Checkbox>
						<Checkbox className={style.checkBox} checked={showArchived} onChange={toggleShowArchived}>
							<Text translationNameSpace="modals" translationKey="showArchived" asFragment={true} />
						</Checkbox>
						<SearchField
							onChangeSelectSearchString={setSearchString}
							searchString={searchString}
							className={style.searchField}
						/>
					</div>
					{menu}
				</>
			);
		},
		[
			visibleData,
			IDsSelectedRestaurant,
			accessibleRestaurants,
			hasAccess,
			handleSelectAllData,
			setSearchString,
			searchString,
			showArchived,
			toggleShowArchived,
		],
	);

	return (
		<FormSelect
			testId="RestaurantsMultipleSelect"
			name="selectedRestaurants"
			label="restaurantsLabel"
			options={mappedOptions}
			fetchError={responseError}
			loading={isLoading}
			onRefetch={refetch}
			disabled={!IDSelectedCustomer || isLoading}
			onChange={setIDsRestaurant}
			counterValue={IDsSelectedRestaurant.length}
			validationRules={validationRules}
			dropdownRender={renderSelectMenu}
			mode="multiple"
		/>
	);
};

export default React.memo(MultipleSelect);
